/* eslint react-hooks/exhaustive-deps: "warn" */

import Head from 'next/head';
import dynamic from 'next/dynamic';

import { fontClassNames } from 'font';

import RouteProgress from 'libs/router/RouteProgress';
import { withDomainSuffix } from 'libs/seo';

import { UserCountryProvider } from 'context/UserCountryContext';
import { LeadProvider } from 'context/LeadContext';
import { ConsentProvider } from 'context/ConsentContext';
import { ContentProvider, initialValues as initialContentValues } from 'context/ContentContext';

import { DynamicImport } from 'ui/DynamicImport';

import { FooterLoader } from 'features/layout/organisms/Footer/FooterLoader/FooterLoader';
import { Layout, LayoutContainer, LayoutContent } from 'features/layout/organisms/Layout';
import Header from 'features/layout/organisms/Header/Header';

import { HubRouteProps } from './HubRoute.types';
import { HubListPage } from './HubListPage';
import { HubTopicPage } from './HubTopicPage';

type Props = HubRouteProps & { lang: string };

/**
 * Renders a resolved hub page (topic or list) inside the same site chrome the
 * CMS Page.tsx and landing-pages StaticPageRoute build - Header, Footer and
 * providers - but driven by content.json instead of a CMS record. The hub is
 * English-only for now; translation arrives via a localized content.json, at
 * which point internationalization/links can flow through like other pages.
 */
export const HubRoute = ({ data, meta, lang }: Props) => {
  const title = withDomainSuffix(meta.title);
  const description = withDomainSuffix(meta.description);

  return (
    <>
      <RouteProgress />

      <Head>
        <meta name="viewport" content="width=device-width" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {meta.notIndexed && <meta name="robots" content="noindex, follow" />}
        {/* canonical is emitted globally from asPath in pages/_app.tsx */}
      </Head>

      <main data-page-type="ssr" className={fontClassNames}>
        <UserCountryProvider>
          <LeadProvider>
            <ConsentProvider>
              <ContentProvider
                {...initialContentValues}
                lang={lang}
                data={{} as (typeof initialContentValues)['data']}
                page={1}
                pathname=""
                additionals={{} as (typeof initialContentValues)['additionals']}
                clientNavigationGroups={{}}
              >
                <Layout>
                  <LayoutContainer>
                    <Header />

                    <LayoutContent>
                      {data.kind === 'topic' ? (
                        <HubTopicPage
                          topic={data.topic}
                          group={data.group}
                          related={data.related}
                          readingPath={data.readingPath}
                          guides={data.guides}
                          lang={lang}
                        />
                      ) : (
                        <HubListPage data={data} lang={lang} />
                      )}
                    </LayoutContent>
                  </LayoutContainer>

                  <DynamicImport fallback={() => <FooterLoader />}>
                    {() => {
                      const FooterComponent = dynamic(() => import('features/layout/organisms/Footer/Footer'), {
                        ssr: false,
                        loading: () => <FooterLoader />,
                      });

                      return <FooterComponent hasBookDemo />;
                    }}
                  </DynamicImport>
                </Layout>
              </ContentProvider>
            </ConsentProvider>
          </LeadProvider>
        </UserCountryProvider>
      </main>
    </>
  );
};
